import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, FolderPlus, Link2, Pencil, Plus, Star, Trash2, X } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { LINK_AUDIENCE_LABELS, linkSchema, nameSchema, ROLE_LABELS } from '@blimo/shared';
import {
  applyProblemToForm,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CheckboxField,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Form,
  FormRootError,
  InputField,
  PageHeader,
  SearchInput,
  SelectField,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
  TextareaField,
  toast,
} from '@blimo/ui';

/** Administration › Nützliche Links (F-1003): Kategorien und Links pflegen; das Portal zeigt aktive Links je Kategorie. */
export function LinksAdminPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const query = useQuery(adminQueries.links(api));
  const [linkSheet, setLinkSheet] = useState(/** @type {{ link?: any, categoryId?: string } | null} */ (null));
  const [categoryDialog, setCategoryDialog] = useState(/** @type {{ category?: any } | null} */ (null));
  const [deleteCategory, setDeleteCategory] = useState(/** @type {any} */ (null));
  const [deleteLink, setDeleteLink] = useState(/** @type {any} */ (null));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.admin.resource('links') });

  const categories = query.data?.data ?? [];
  const uncategorized = query.data?.meta?.uncategorized ?? [];

  async function removeCategory() {
    try {
      await api.admin.links.removeCategory(deleteCategory.id);
      toast.success('Kategorie gelöscht');
      await invalidate();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Kategorie konnte nicht gelöscht werden.'));
    }
  }
  async function removeLink() {
    try {
      await api.admin.links.remove(deleteLink.id);
      toast.success('Link gelöscht');
      await invalidate();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Link konnte nicht gelöscht werden.'));
    }
  }

  const sections = [
    ...categories.map((c) => ({ key: `c-${c.id}`, category: c, title: c.name, links: c.links ?? [] })),
    ...(uncategorized.length > 0 ? [{ key: 'none', category: null, title: 'Ohne Kategorie', links: uncategorized }] : []),
  ];

  return (
    <>
      <PageHeader
        title="Nützliche Links"
        description="Links für das Kundenportal (Bereich „Service“) – optional nur für bestimmte Rollen oder einzelne Benutzer und als Schaltfläche auf der Startseite."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setCategoryDialog({})}>
              <FolderPlus aria-hidden="true" /> Kategorie anlegen
            </Button>
            <Button size="sm" onClick={() => setLinkSheet({})}>
              <Plus aria-hidden="true" /> Link anlegen
            </Button>
          </div>
        }
      />
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : sections.length === 0 ? (
        <EmptyState icon={Link2} title="Noch keine Links" description="Legen Sie eine Kategorie und die ersten Links an." />
      ) : (
        <div className="flex flex-col gap-3">
          {sections.map((section) => (
            <Card key={section.key} role="region" aria-label={section.title}>
              <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
                <div className="flex flex-col gap-1">
                  <CardTitle as="h2">{section.title}</CardTitle>
                  {!section.category && (
                    <CardDescription>Erscheinen im Portal unter „Weitere Links“ (hervorgehobene Links zusätzlich auf der Startseite).</CardDescription>
                  )}
                </div>
                {section.category && (
                  <div className="flex flex-wrap gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setLinkSheet({ categoryId: String(section.category.id) })}>
                      <Plus aria-hidden="true" /> Link
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setCategoryDialog({ category: section.category })}
                      aria-label={`Kategorie ${section.title} umbenennen`}
                    >
                      <Pencil aria-hidden="true" /> Umbenennen
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteCategory(section.category)} aria-label={`Kategorie ${section.title} löschen`}>
                      <Trash2 aria-hidden="true" /> Löschen
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {section.links.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Keine Links in dieser Kategorie.</p>
                ) : (
                  <ul aria-label={`Links in ${section.title}`} className="flex flex-col divide-y">
                    {section.links.map((link) => (
                      <li key={link.id} className="flex flex-wrap items-start gap-2 py-2 text-sm">
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className="flex flex-wrap items-center gap-2">
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                            >
                              {link.name}
                              <ExternalLink className="size-3.5" aria-hidden="true" />
                            </a>
                            {link.is_active === false ? <Badge variant="neutral">Inaktiv</Badge> : <Badge variant="success">Aktiv</Badge>}
                            {link.is_highlighted && (
                              <Badge variant="info">
                                <Star className="size-3" aria-hidden="true" /> Startseite
                              </Badge>
                            )}
                            {link.audience && link.audience !== 'all' && <Badge variant="secondary">{audienceSummary(link)}</Badge>}
                          </span>
                          <span className="break-all text-xs text-muted-foreground">{link.url}</span>
                          {link.description && <span className="text-muted-foreground">{link.description}</span>}
                        </span>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setLinkSheet({ link })} aria-label={`${link.name} bearbeiten`}>
                            <Pencil aria-hidden="true" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setDeleteLink(link)} aria-label={`${link.name} löschen`}>
                            <Trash2 aria-hidden="true" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Sheet open={Boolean(linkSheet)} onOpenChange={(open) => !open && setLinkSheet(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{linkSheet?.link ? 'Link bearbeiten' : 'Link anlegen'}</SheetTitle>
            <SheetDescription>Externe Adresse mit http:// oder https://.</SheetDescription>
          </SheetHeader>
          {linkSheet && (
            <LinkForm
              link={linkSheet.link}
              defaultCategoryId={linkSheet.categoryId}
              categories={categories}
              onDone={() => setLinkSheet(null)}
              onSaved={invalidate}
            />
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={Boolean(categoryDialog)} onOpenChange={(open) => !open && setCategoryDialog(null)}>
        <DialogContent>
          {categoryDialog && <CategoryForm category={categoryDialog.category} onDone={() => setCategoryDialog(null)} onSaved={invalidate} />}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteCategory)}
        onOpenChange={(open) => !open && setDeleteCategory(null)}
        title="Kategorie löschen?"
        description={
          deleteCategory
            ? `Die Kategorie „${deleteCategory.name}“ wird gelöscht. Ihre Links bleiben erhalten (ohne Kategorie) und erscheinen dann nicht mehr im Portal.`
            : undefined
        }
        affectedCount={deleteCategory?.links?.length}
        confirmLabel="Löschen"
        destructive
        onConfirm={removeCategory}
      />
      <ConfirmDialog
        open={Boolean(deleteLink)}
        onOpenChange={(open) => !open && setDeleteLink(null)}
        title="Link löschen?"
        description={deleteLink ? `„${deleteLink.name}“ wird endgültig gelöscht.` : undefined}
        confirmLabel="Löschen"
        destructive
        onConfirm={removeLink}
      />
    </>
  );
}

function CategoryForm({ category, onDone, onSaved }) {
  const api = useApi();
  const form = useForm({ resolver: zodResolver(nameSchema), defaultValues: { name: category?.name ?? '' } });
  const submit = form.handleSubmit(async ({ name }) => {
    try {
      if (category) await api.admin.links.updateCategory(category.id, { name: name.trim() });
      else await api.admin.links.createCategory({ name: name.trim() });
      toast.success(category ? 'Kategorie umbenannt' : 'Kategorie angelegt');
      await onSaved();
      onDone();
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['name'] });
    }
  });
  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{category ? 'Kategorie umbenennen' : 'Kategorie anlegen'}</DialogTitle>
          <DialogDescription>Kategorien gliedern die Links im Portal.</DialogDescription>
        </DialogHeader>
        <FormRootError />
        <InputField name="name" label="Name der Kategorie" autoComplete="off" />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting}>
            Speichern
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

function LinkForm({ link, defaultCategoryId, categories, onDone, onSaved }) {
  const api = useApi();
  const form = useForm({
    resolver: zodResolver(linkSchema),
    defaultValues: {
      name: link?.name ?? '',
      url: link?.url ?? '',
      description: link?.description ?? '',
      link_category_id: link?.link_category_id ? String(link.link_category_id) : (defaultCategoryId ?? ''),
      is_active: link ? link.is_active !== false : true,
      is_highlighted: Boolean(link?.is_highlighted),
      audience: link?.audience ?? 'all',
      roles: link?.roles ?? [],
      user_ids: (link?.users ?? []).map((u) => String(u.id)),
    },
  });
  const [knownUsers, setKnownUsers] = useState(() => Object.fromEntries((link?.users ?? []).map((u) => [String(u.id), u])));
  const options = categories.map((c) => ({ value: String(c.id), label: c.name }));
  const submit = form.handleSubmit(async (values) => {
    const payload = {
      name: values.name.trim(),
      url: values.url.trim(),
      description: values.description.trim() || null,
      link_category_id: values.link_category_id ? Number(values.link_category_id) : null,
      is_active: values.is_active,
      is_highlighted: Boolean(values.is_highlighted),
      audience: values.audience ?? 'all',
      ...(values.audience === 'roles' ? { roles: values.roles } : {}),
      ...(values.audience === 'users' ? { user_ids: (values.user_ids ?? []).map(Number) } : {}),
    };
    try {
      if (link) await api.admin.links.update(link.id, payload);
      else await api.admin.links.create(payload);
      toast.success(link ? 'Link gespeichert' : 'Link angelegt');
      await onSaved();
      onDone();
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['name', 'url', 'description', 'link_category_id', 'is_active', 'audience', 'roles', 'user_ids'] });
    }
  });
  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-1 flex-col gap-4 px-4">
        <FormRootError />
        <InputField name="name" label="Name" autoComplete="off" />
        <InputField name="url" label="Adresse (URL)" type="url" placeholder="https://" autoComplete="off" />
        <TextareaField name="description" label="Beschreibung" rows={3} />
        <SelectField name="link_category_id" label="Kategorie" options={options} placeholder="Ohne Kategorie" />
        <CheckboxField name="is_active" label="Aktiv (im Portal sichtbar)" />
        <CheckboxField
          name="is_highlighted"
          label="Auf der Startseite hervorheben"
          description="Erscheint im Portal zusätzlich als Schaltfläche auf der Übersicht (z. B. Sonderzugang für einzelne Benutzer)."
        />
        <AudienceFields form={form} knownUsers={knownUsers} onKnownUsers={setKnownUsers} />
        <SheetFooter className="px-0">
          <Button type="submit" loading={form.formState.isSubmitting}>
            Speichern
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}

const AUDIENCE_OPTIONS = Object.entries(LINK_AUDIENCE_LABELS).map(([value, label]) => ({ value, label }));
const PORTAL_LINK_ROLES = ['owner', 'investor', 'auditor', 'meter-reader'];

/** "Rollen: Eigentümer, …" / "Nur Name" / "3 Benutzer" @param {any} link */
function audienceSummary(link) {
  if (link.audience === 'roles') return `Rollen: ${(link.roles ?? []).map((r) => ROLE_LABELS[r] ?? r).join(', ')}`;
  if (link.audience === 'users') {
    const users = link.users ?? [];
    return users.length === 1 ? `Nur ${users[0].name}` : `${users.length} Benutzer`;
  }
  return LINK_AUDIENCE_LABELS.all;
}

/** Zielgruppe (F-1005): alle | Rollen | einzelne Benutzer – die API filtert im Portal serverseitig. */
function AudienceFields({ form, knownUsers, onKnownUsers }) {
  const audience = useWatch({ control: form.control, name: 'audience' });
  const roles = useWatch({ control: form.control, name: 'roles' }) ?? [];
  const userIds = useWatch({ control: form.control, name: 'user_ids' }) ?? [];
  const errors = form.formState.errors;
  const opts = { shouldDirty: true, shouldValidate: form.formState.isSubmitted };
  const toggleRole = (role, checked) => form.setValue('roles', checked ? [...roles, role] : roles.filter((r) => r !== role), opts);
  const setUsers = (ids) => form.setValue('user_ids', ids, opts);

  return (
    <fieldset className="flex flex-col gap-3 rounded-md border p-3">
      <legend className="px-1 text-sm font-medium">Sichtbar für</legend>
      <SelectField name="audience" label="Zielgruppe" options={AUDIENCE_OPTIONS} />
      {audience === 'roles' && (
        <div className="flex flex-col gap-1.5" role="group" aria-label="Rollen">
          {PORTAL_LINK_ROLES.map((role) => (
            <label key={role} className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="size-4 accent-primary" checked={roles.includes(role)} onChange={(e) => toggleRole(role, e.target.checked)} />
              {ROLE_LABELS[role] ?? role}
            </label>
          ))}
          {errors.roles?.message && <p className="text-xs text-destructive">{String(errors.roles.message)}</p>}
        </div>
      )}
      {audience === 'users' && (
        <UserMultiPicker
          selected={userIds}
          known={knownUsers}
          onAdd={(user) => {
            onKnownUsers((k) => ({ ...k, [String(user.id)]: user }));
            if (!userIds.includes(String(user.id))) setUsers([...userIds, String(user.id)]);
          }}
          onRemove={(id) => setUsers(userIds.filter((u) => u !== id))}
          error={errors.user_ids?.message ? String(errors.user_ids.message) : null}
        />
      )}
    </fieldset>
  );
}

function UserMultiPicker({ selected, known, onAdd, onRemove, error }) {
  const api = useApi();
  const [q, setQ] = useState('');
  const searching = q.trim().length >= 2;
  const results = useQuery({ ...adminQueries.users(api, { per_page: 8, filter: { q } }), enabled: searching });
  return (
    <div className="flex flex-col gap-2">
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Ausgewählte Benutzer">
          {selected.map((id) => (
            <li key={id}>
              <Badge variant="secondary" className="gap-1 pr-1">
                {known[id]?.name ?? `Benutzer #${id}`}
                <button type="button" className="rounded p-0.5 hover:bg-muted" aria-label={`${known[id]?.name ?? id} entfernen`} onClick={() => onRemove(id)}>
                  <X className="size-3" aria-hidden="true" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      )}
      <SearchInput label="Benutzer suchen" placeholder="Name oder E-Mail (mind. 2 Zeichen) …" value={q} onValueChange={setQ} />
      {searching && (
        <ul className="flex max-h-48 flex-col overflow-y-auto rounded-md border text-sm" aria-label="Suchergebnisse">
          {results.isPending ? (
            <li className="p-2 text-muted-foreground">Suche …</li>
          ) : (results.data?.data ?? []).length === 0 ? (
            <li className="p-2 text-muted-foreground">Keine Benutzer gefunden</li>
          ) : (
            results.data.data.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-2 py-1.5 text-left hover:bg-muted disabled:opacity-50"
                  disabled={selected.includes(String(u.id))}
                  onClick={() => onAdd({ id: u.id, name: u.name, email: u.email })}
                >
                  <span className="font-medium">{u.name}</span>
                  <span className="text-xs text-muted-foreground">{u.email}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
